import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { EVENT_NAMES } from '@/event-manager/types';
import {
  EventConfig,
  useEventManagerRegister,
} from '@/event-manager/use-event-manager-register';
import { useStore } from '@/store/useStore';

import {
  isGeolocationInvalidated,
  isGeolocationPassed,
  isGeolocationRejected,
  isLocationFailure,
  isLocationInProgress,
  type GeolocationResponse,
} from './types';
import useGeolocationCallbackHandlers from './use-geolocation-callback-handlers';

export type { GeolocationResponse } from './types';

export const useGeolocationCallback = () => {
  const { isAndroid, isXsell } = useStore(
    useShallow((s) => ({
      isAndroid: s.runtime.wrapperType.isAndroid,
      isXsell: s.runtime.wrapperType.isXsell,
    })),
  );

  const {
    handleGeolocationPassed,
    handleGeolocationInProgress,
    handleGeolocationFailed,
    handleGeolocationRejected,
    handleGeolocationInvalidated,
    handleXsellGeolocationUpdate,
  } = useGeolocationCallbackHandlers();

  const geolocationCallback = useCallback(
    (action: GeolocationResponse) => {
      const { type, payload } = action;

      switch (type) {
        case isLocationInProgress(type):
          handleGeolocationInProgress();
          break;

        case isGeolocationPassed(type):
          handleGeolocationPassed(payload);
          break;

        case isLocationFailure(type):
          handleGeolocationFailed(payload);
          break;

        case isGeolocationRejected(type):
          handleGeolocationRejected(payload);
          break;

        case isGeolocationInvalidated(type):
          handleGeolocationInvalidated(payload);
          break;
      }
    },
    [
      handleGeolocationFailed,
      handleGeolocationInProgress,
      handleGeolocationPassed,
      handleGeolocationRejected,
      handleGeolocationInvalidated,
    ],
  );

  const handleAndroidGeolocationEvent = useCallback(
    (event: Event) => {
      const detail = (event as CustomEvent<GeolocationResponse>).detail;
      geolocationCallback(detail);
    },
    [geolocationCallback],
  );

  const handleXsellGeocomplyEvent = useCallback(() => {
    handleXsellGeolocationUpdate();
  }, [handleXsellGeolocationUpdate]);

  const nativeWrapperEvents = useMemo(() => {
    const events: Array<EventConfig> = [];

    if (isAndroid) {
      events.push({
        eventName: EVENT_NAMES.GEOLOCATION_RESULT,
        type: '*' as const,
        handler: handleAndroidGeolocationEvent,
      });
    }

    if (isXsell) {
      events.push({
        eventName: EVENT_NAMES.GEOLOCATION_UPDATE,
        type: '*' as const,
        handler: handleXsellGeocomplyEvent,
      });
    }

    return events;
  }, [
    isAndroid,
    isXsell,
    handleAndroidGeolocationEvent,
    handleXsellGeocomplyEvent,
  ]);

  useEventManagerRegister(nativeWrapperEvents);

  return { geolocationCallback };
};
